<script setup lang="ts">
/**
 * Page dots („Seitenpunkte“): one small dot per entry of a short sequence — the
 * slides of a playlist, the pages of a carousel, the steps of a tour — with the
 * current one drawn wider as a pill. `v-model` is the zero-based index of the
 * current entry; a click on a dot sets it.
 *
 * Give either `count` or `items`; `items` carry a `label` per entry, which
 * names the dot and is its tooltip. Each dot is a button named „Eintrag 2 von
 * 5“ (kit text `common.dot_pager.dot`) or, with `items`, by its label;
 * `dotLabel` overrides both. The current dot states `aria-current="step"`.
 *
 * Keyboard: the dots are one tab stop (the current dot); ←/→ (↑/↓) move the
 * focus, Home/End jump to the first/last dot, Enter/Space select. `disabled`
 * shows the position but blocks every dot (e.g. while advancing is automatic).
 *
 * For page numbers with previous/next use a pagination; for a process with
 * named stages `NxProcessSteps`.
 */
import { computed, inject, nextTick, ref, watch } from 'vue';
import { i18nInjectionKey } from '../internal';
import type { NxDotPagerItem } from './types';

const props = withDefaults(
  defineProps<{
    /** Number of entries; ignored when `items` is set. */
    count?: number;
    /** One entry per dot; its `label` names the dot. */
    items?: readonly NxDotPagerItem[];
    /** Accessible name of the whole group („Ansichten“). */
    label?: string;
    /** Accessible name of one dot; `index` is zero-based. Wins over `items` labels. */
    dotLabel?: (index: number, count: number) => string;
    /** Shows the position but blocks every dot. */
    disabled?: boolean;
  }>(),
  {
    count: 0,
    items: undefined,
    label: undefined,
    dotLabel: undefined,
    disabled: false,
  }
);

/** Zero-based index of the current entry. */
const model = defineModel<number>({ required: true });

const i18n = inject(i18nInjectionKey, null);
function tr(key: string, fallback: string, params?: Record<string, unknown>): string {
  const full = `common.dot_pager.${key}`;
  const text = i18n?.t(full, params);
  if (text && text !== full) return text;
  return params
    ? fallback.replace(/\{(\w+)\}/g, (_, name: string) => String(params[name] ?? ''))
    : fallback;
}

const total = computed(() => Math.max(0, Math.floor(props.items?.length ?? props.count)));
const indices = computed(() => Array.from({ length: total.value }, (_, index) => index));
const groupLabel = computed(() => props.label ?? tr('label', 'Seiten'));

function nameOf(index: number): string {
  if (props.dotLabel) return props.dotLabel(index, total.value);
  const itemLabel = props.items?.[index]?.label;
  if (itemLabel) return itemLabel;
  return tr('dot', 'Eintrag {index} von {count}', { index: index + 1, count: total.value });
}

/** The dot that holds the tab stop: the current one until the arrows move it. */
const focusIndex = ref(0);
watch(
  [model, total],
  ([current, count]) => {
    focusIndex.value = Math.min(Math.max(0, current ?? 0), Math.max(0, count - 1));
  },
  { immediate: true }
);

const dots = ref<HTMLButtonElement[]>([]);

function select(index: number): void {
  if (props.disabled || index < 0 || index >= total.value) return;
  focusIndex.value = index;
  model.value = index;
}

function onKeydown(event: KeyboardEvent): void {
  if (props.disabled || total.value === 0) return;
  const last = total.value - 1;
  let next: number;
  if (event.key === 'ArrowRight' || event.key === 'ArrowDown')
    next = Math.min(last, focusIndex.value + 1);
  else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp')
    next = Math.max(0, focusIndex.value - 1);
  else if (event.key === 'Home') next = 0;
  else if (event.key === 'End') next = last;
  else return;
  event.preventDefault();
  focusIndex.value = next;
  void nextTick(() => dots.value[next]?.focus());
}
</script>

<template>
  <div
    class="nx-dot-pager"
    :class="{ 'is-disabled': props.disabled }"
    role="group"
    :aria-label="groupLabel"
    data-testid="nx-dot-pager"
    @keydown="onKeydown"
  >
    <button
      v-for="index in indices"
      :key="index"
      ref="dots"
      type="button"
      class="nx-dot-pager__dot"
      :class="{ 'is-current': index === model }"
      :tabindex="index === focusIndex ? 0 : -1"
      :disabled="props.disabled"
      :aria-label="nameOf(index)"
      :title="props.items?.[index]?.label"
      :aria-current="index === model ? 'step' : undefined"
      :data-index="index"
      @click="select(index)"
    >
      <span class="nx-dot-pager__mark" aria-hidden="true" />
    </button>
  </div>
</template>

<style scoped>
.nx-dot-pager {
  --dot-size: calc(var(--nxt-size-unit) * 0.5);
  --dot-current-width: calc(var(--nxt-size-unit) * 1.375);

  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  min-width: 0;
}

.nx-dot-pager__dot {
  display: inline-grid;
  place-items: center;
  margin: 0;
  padding: var(--nxt-space-2xs);
  border: 0;
  border-radius: var(--nxt-radius-full);
  background: transparent;
  color: inherit;
  cursor: pointer;
}
.nx-dot-pager__dot:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -1);
}
.nx-dot-pager__dot:disabled {
  cursor: default;
}

.nx-dot-pager__mark {
  display: block;
  width: var(--dot-size);
  height: var(--dot-size);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-border-strong);
  transition:
    width var(--nxt-motion-fast) var(--nxt-ease-out),
    background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-dot-pager__dot:hover:not(:disabled) .nx-dot-pager__mark {
  background: var(--nxt-fg-muted);
}
.nx-dot-pager__dot.is-current .nx-dot-pager__mark {
  width: var(--dot-current-width);
  background: var(--nxt-brand-bg);
}
.nx-dot-pager.is-disabled .nx-dot-pager__mark {
  opacity: 0.6;
}
.nx-dot-pager.is-disabled .nx-dot-pager__dot.is-current .nx-dot-pager__mark {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .nx-dot-pager__mark {
    transition: none;
  }
}
</style>
